import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  alpha,
  Skeleton,
} from '@mui/material'
import { Newspaper, ExternalLink, Globe } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { backendClient } from '../../../api/backend'
import { fetchUserCountry } from '../../../redux/actions/newsAction'

// Timezone → country fallback (same as newsAction.js)
const TZ_COUNTRY_MAP = {
  'Africa/Casablanca': 'MA',
  'Asia/Dubai': 'AE',
  'Asia/Riyadh': 'SA',
  'Asia/Bahrain': 'BH',
  'Europe/Paris': 'FR',
  'Africa/Tunis': 'TN',
  'Africa/Algiers': 'DZ',
  'Africa/Cairo': 'EG',
  'Asia/Beirut': 'LB',
  'Asia/Amman': 'JO',
  'Asia/Kuwait': 'KW',
  'Asia/Qatar': 'QA',
  'Asia/Muscat': 'OM',
}

const COUNTRY_FLAGS = {
  MA: '\u{1F1F2}\u{1F1E6}',
  AE: '\u{1F1E6}\u{1F1EA}',
  SA: '\u{1F1F8}\u{1F1E6}',
  BH: '\u{1F1E7}\u{1F1ED}',
  FR: '\u{1F1EB}\u{1F1F7}',
  TN: '\u{1F1F9}\u{1F1F3}',
  DZ: '\u{1F1E9}\u{1F1FF}',
  EG: '\u{1F1EA}\u{1F1EC}',
}

function NewsItemRow({ item, onClick }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const goldColor = theme.palette.primary.main

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
    } catch {
      return ''
    }
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        gap: 1.5,
        py: 1.25,
        px: 1.5,
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
        '&:hover': {
          background: alpha(goldColor, 0.04),
        },
        '&:not(:last-child)': {
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        },
      }}
    >
      {item.image_url ? (
        <Box
          component="img"
          src={item.image_url}
          alt=""
          sx={{
            width: 48,
            height: 48,
            borderRadius: '8px',
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '8px',
            background: alpha(goldColor, 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Newspaper size={18} color={goldColor} />
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
            fontSize: '0.8rem',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: 0.5,
          }}
        >
          {item.title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {item.source_name && (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, fontSize: '0.68rem', fontWeight: 500 }}
            >
              {item.source_name}
            </Typography>
          )}
          {item.source_name && item.published_at && (
            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.6rem' }}>
              {'·'}
            </Typography>
          )}
          {item.published_at && (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.disabled, fontSize: '0.68rem' }}
            >
              {formatDate(item.published_at)}
            </Typography>
          )}
          {item.categories?.[0] && (
            <>
              <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.6rem' }}>
                {'·'}
              </Typography>
              <Box
                sx={{
                  px: 0.75,
                  py: 0.1,
                  borderRadius: '4px',
                  background: alpha(theme.palette.info.main, 0.08),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.info.main,
                    fontSize: '0.62rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  }}
                >
                  {t(`news_category.${item.categories[0]}`, item.categories[0].replace(/_/g, ' '))}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

function ListSkeleton({ rows = 4 }) {
  return (
    <Box sx={{ px: 1.5, py: 1 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1.25 }}>
          <Skeleton variant="rectangular" width={48} height={48} sx={{ borderRadius: '8px', flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="90%" height={14} />
            <Skeleton variant="text" width="60%" height={14} sx={{ mt: 0.5 }} />
            <Skeleton variant="text" width="40%" height={12} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
      ))}
    </Box>
  )
}

const LegalNewsWidget = () => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const goldColor = theme.palette.primary.main

  const ipDetectedCountry = useSelector((state) => state.legalNews?.detectedCountry)
  // The user's explicit country preference (search/chat country picker) wins over IP detection
  const countryPref = useSelector((state) => state.user?.countryPreference)
  const preferredCountry = Array.isArray(countryPref) ? countryPref[0] : countryPref
  const detectedCountry = preferredCountry || ipDetectedCountry
  const [news, setNews] = useState([])
  const [loading, setLoading] = useState(true)
  const [userCountry, setUserCountry] = useState(null)

  // Detect user country
  useEffect(() => {
    if (detectedCountry) {
      setUserCountry(detectedCountry)
      return
    }
    // Dispatch the existing action to detect country
    dispatch(fetchUserCountry())
    // Also try timezone fallback locally
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      const detected = TZ_COUNTRY_MAP[tz]
      if (detected) setUserCountry(detected)
    } catch {
      // ignore
    }
  }, [detectedCountry, dispatch])

  // Update from redux when it arrives
  useEffect(() => {
    if (detectedCountry && detectedCountry !== userCountry) {
      setUserCountry(detectedCountry)
    }
  }, [detectedCountry, userCountry])

  // Fetch news
  useEffect(() => {
    async function fetchNews() {
      setLoading(true)
      try {
        const params = {
          skip: 0,
          limit: 6,
          days: 30,
          language: i18n.language === 'ar' ? 'ar' : i18n.language === 'fr' ? 'fr' : 'en',
        }
        if (userCountry) {
          params.country = userCountry
        }
        const response = await backendClient.fetchNews(params)
        const items = response.items ?? response
        setNews(Array.isArray(items) ? items.slice(0, 6) : [])
      } catch (err) {
        console.error('Error fetching dashboard news:', err)
        setNews([])
      } finally {
        setLoading(false)
      }
    }
    fetchNews()
  }, [userCountry, i18n.language])

  const countryFlag = COUNTRY_FLAGS[userCountry] || ''

  const cardSx = {
    elevation: 0,
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 2px 8px rgba(0, 0, 0, 0.04)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  }

  return (
    <Card elevation={0} sx={cardSx}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Newspaper size={15} color={goldColor} />
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '0.82rem' }}
          >
            {t('legal_news') || 'Legal News'}
          </Typography>
          {countryFlag && (
            <Typography sx={{ fontSize: '0.9rem', lineHeight: 1 }}>{countryFlag}</Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Box
            onClick={() => navigate('/legal-news')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.4,
              cursor: 'pointer',
              color: goldColor,
              '&:hover': { opacity: 0.7 },
            }}
          >
            <ExternalLink size={11} />
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
              {t('view_all') || 'View all'}
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ maxHeight: 380, overflowY: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-track': { background: 'transparent' }, '&::-webkit-scrollbar-thumb': { background: alpha(goldColor, 0.2), borderRadius: '4px' } }}>
          {loading ? (
            <ListSkeleton rows={6} />
          ) : news.length > 0 ? (
            <Box sx={{ pb: 1 }}>
              {news.map((item) => (
                <NewsItemRow
                  key={item.id}
                  item={item}
                  onClick={() => navigate(`/legal-news/${item.id}`)}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Globe size={28} color={theme.palette.text.disabled} />
              <Typography
                variant="body2"
                sx={{ mt: 1, color: theme.palette.text.disabled, fontSize: '0.8rem' }}
              >
                {t('no_news_available') || 'No news available'}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default LegalNewsWidget
